/**
 * Entity Field Definitions
 *
 * This file defines metadata for entity fields, including:
 * - Which fields are public (filterable/exportable)
 * - Field types for appropriate filter controls
 * - Display labels for UI (using translation keys)
 */

export type FieldType = 'text' | 'select' | 'boolean' | 'date' | 'number' | 'array' | 'foreignKey';

export interface EntityField {
  key: string;              // Field path (e.g., 'name', 'user.email')
  labelKey: string;         // Translation key for label (e.g., 'common.name')
  type: FieldType;          // Field type for filter control
  public: boolean;          // Is this field exportable?
  filterable?: boolean;     // Is this field filterable? (defaults to true for public fields)
  options?: { value: string; labelKey: string }[]; // For select fields (uses translation keys)
  placeholderKey?: string;  // Translation key for placeholder
  foreignKeyEntity?: string; // For foreignKey type: 'users', 'groups', etc.
}

export interface EntityDefinition {
  name: string;
  fields: EntityField[];
}

// Member entity fields
export const memberFields: EntityField[] = [
  { key: 'name', labelKey: 'common.name', type: 'text', public: true, placeholderKey: 'filter.placeholders.name' },
  { key: 'firstName', labelKey: 'members.firstName', type: 'text', public: true, filterable: false },
  { key: 'lastName', labelKey: 'members.lastName', type: 'text', public: true, filterable: false },
  { key: 'description', labelKey: 'common.description', type: 'text', public: true, placeholderKey: 'filter.placeholders.description' },
  { key: 'color', labelKey: 'filter.fields.color', type: 'text', public: false },
  { key: 'user', labelKey: 'members.userCaregiver', type: 'foreignKey', public: true, foreignKeyEntity: 'users' },
  { key: 'user.name', labelKey: 'export.responsiblePersonName', type: 'text', public: true, filterable: false },
  { key: 'user.email', labelKey: 'export.responsiblePersonEmail', type: 'text', public: true, filterable: false },
  { key: 'user.phoneNumber', labelKey: 'export.responsiblePersonPhone', type: 'text', public: true, filterable: false },
  { key: 'groups', labelKey: 'members.assignGroups', type: 'foreignKey', public: true, foreignKeyEntity: 'groups' },
  { key: 'city', labelKey: 'members.city', type: 'text', public: true, filterable: false },
  { key: 'cityDistrict', labelKey: 'members.cityDistrict', type: 'text', public: true, filterable: false },
  { key: 'street', labelKey: 'members.street', type: 'text', public: true, filterable: false },
  { key: 'buildingNumber', labelKey: 'members.buildingNumber', type: 'text', public: true, filterable: false },
  { key: 'streetNumber', labelKey: 'members.streetNumber', type: 'text', public: true, filterable: false },
  { key: 'zipCode', labelKey: 'members.zipCode', type: 'text', public: true, filterable: false },
  { key: 'nationality', labelKey: 'members.nationality', type: 'text', public: true, filterable: false },
  { key: 'birthNumber', labelKey: 'members.birthNumber', type: 'text', public: true, filterable: false },
  { key: 'createdAt', labelKey: 'filter.fields.createdAt', type: 'date', public: false },
];

// User entity fields
export const userFields: EntityField[] = [
  { key: 'name', labelKey: 'common.name', type: 'text', public: true, placeholderKey: 'filter.placeholders.name' },
  { key: 'email', labelKey: 'common.email', type: 'text', public: true, placeholderKey: 'filter.placeholders.email' },
  { key: 'role', labelKey: 'users.role', type: 'foreignKey', public: true, foreignKeyEntity: 'roles' },
  { key: 'createdAt', labelKey: 'filter.fields.createdAt', type: 'date', public: false },
];

// Group entity fields
export const groupFields: EntityField[] = [
  { key: 'name', labelKey: 'common.name', type: 'text', public: true, placeholderKey: 'filter.placeholders.name' },
  { key: 'description', labelKey: 'common.description', type: 'text', public: true, placeholderKey: 'filter.placeholders.description' },
  { key: 'createdAt', labelKey: 'filter.fields.createdAt', type: 'date', public: false },
];

// Event entity fields
export const eventFields: EntityField[] = [
  { key: 'name', labelKey: 'common.name', type: 'text', public: true, placeholderKey: 'filter.placeholders.name' },
  { key: 'description', labelKey: 'common.description', type: 'text', public: true, placeholderKey: 'filter.placeholders.description' },
  { key: 'startTime', labelKey: 'events.startTime', type: 'date', public: true },
  { key: 'endTime', labelKey: 'events.endTime', type: 'date', public: false },
  { key: 'responsible', labelKey: 'events.responsible', type: 'foreignKey', public: true, foreignKeyEntity: 'admins' },
  { key: 'activityId', labelKey: 'events.activity', type: 'foreignKey', public: true, foreignKeyEntity: 'activities' },
  { key: 'courseId', labelKey: 'events.course', type: 'foreignKey', public: true, foreignKeyEntity: 'courses' },
];

// Event Template entity fields
export const eventTemplateFields: EntityField[] = [
  { key: 'name', labelKey: 'common.name', type: 'text', public: true, placeholderKey: 'filter.placeholders.name' },
  { key: 'description', labelKey: 'common.description', type: 'text', public: true, placeholderKey: 'filter.placeholders.description' },
  { key: 'recurringPattern', labelKey: 'templates.recurrencePattern', type: 'select', public: true, options: [
    { value: 'daily', labelKey: 'templates.daily' },
    { value: 'weekly', labelKey: 'templates.weekly' },
    { value: 'biweekly', labelKey: 'templates.biweekly' },
    { value: 'monthly', labelKey: 'templates.monthly' },
  ]},
  { key: 'activityId', labelKey: 'templates.activity', type: 'foreignKey', public: true, foreignKeyEntity: 'activities' },
  { key: 'courseId', labelKey: 'templates.course', type: 'foreignKey', public: true, foreignKeyEntity: 'courses' },
  { key: 'isActive', labelKey: 'common.active', type: 'boolean', public: true },
];

// Minimal field set for simple tables that only need a quick name search.
// Used with FilterBar in search-only mode (no structured filter panel).
export const nameOnlyFields: EntityField[] = [
  { key: 'name', labelKey: 'common.name', type: 'text', public: true, placeholderKey: 'filter.placeholders.name' },
];

// Helper to get public fields for an entity (for export)
export const getPublicFields = (fields: EntityField[]): EntityField[] => {
  return fields.filter(f => f.public);
};

// Helper to get filterable fields for an entity (for FilterBar)
export const getFilterableFields = (fields: EntityField[]): EntityField[] => {
  return fields.filter(f => f.public && f.filterable !== false);
};

// Helper to get field by key
export const getFieldByKey = (fields: EntityField[], key: string): EntityField | undefined => {
  return fields.find(f => f.key === key);
};

// Filter value types
export interface FilterValue {
  field: string;
  value: string | boolean | null;
  valueTo?: string | null;
  operator?: 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'dateRange';
}

// Apply filters to data
export const applyFilters = <T extends Record<string, any>>(
  data: T[],
  filters: FilterValue[]
): T[] => {
  if (filters.length === 0) return data;

  return data.filter(item => {
    return filters.every(filter => {
      // Handle date range filters early (value can be null when only "to" is set)
      if (filter.operator === 'dateRange') {
        if (!filter.value && !filter.valueTo) return true;
        const fieldValue = getNestedValue(item, filter.field);
        const dateVal = new Date(fieldValue);
        if (isNaN(dateVal.getTime())) return false;
        const ts = dateVal.getTime();

        if (filter.value) {
          const from = new Date(filter.value + 'T00:00:00').getTime();
          if (ts < from) return false;
        }
        if (filter.valueTo) {
          const to = new Date(filter.valueTo + 'T23:59:59.999').getTime();
          if (ts > to) return false;
        }
        return true;
      }

      if (filter.value === null || filter.value === '' || filter.value === undefined) {
        return true; // Skip empty filters
      }

      const fieldValue = getNestedValue(item, filter.field);

      // Handle foreignKey fields (compare by _id)
      // For objects like user: { _id: '123', name: 'John' } or role: { _id: 1 }.
      // Compare as strings so a numeric DB id matches the string option value.
      if (typeof fieldValue === 'object' && fieldValue !== null && fieldValue._id != null) {
        return String(fieldValue._id) === String(filter.value);
      }

      // Handle array fields (like groups) - can filter by _id for foreignKey
      if (Array.isArray(fieldValue)) {
        const searchValue = String(filter.value);
        return fieldValue.some(v => {
          // For foreignKey arrays (e.g., groups), compare by _id
          if (typeof v === 'object' && v._id != null) {
            return String(v._id) === searchValue;
          }
          // For text search in arrays
          if (typeof v === 'object' && v.name) {
            return v.name.toLowerCase().includes(searchValue.toLowerCase());
          }
          return String(v).toLowerCase().includes(searchValue.toLowerCase());
        });
      }

      // Handle boolean fields
      if (typeof filter.value === 'boolean') {
        return fieldValue === filter.value;
      }

      // Handle string comparison
      const itemValue = String(fieldValue || '').toLowerCase();
      const searchValue = String(filter.value).toLowerCase();

      switch (filter.operator) {
        case 'equals':
          return itemValue === searchValue;
        case 'startsWith':
          return itemValue.startsWith(searchValue);
        case 'endsWith':
          return itemValue.endsWith(searchValue);
        case 'contains':
        default:
          return itemValue.includes(searchValue);
      }
    });
  });
};

// Get nested value from object using dot notation
const getNestedValue = (obj: any, path: string): any => {
  const keys = path.split('.');
  let value = obj;

  for (const key of keys) {
    if (value === null || value === undefined) {
      return undefined;
    }
    value = value[key];
  }

  return value;
};
